import React, { useState } from 'react';
import { HelpCircle, ExternalLink, Key, CheckCircle, AlertCircle } from 'lucide-react';

interface BingConnectionTooltipProps {
  userId: string;
  onSuccess?: () => void;
}

const BingConnectionTooltip: React.FC<BingConnectionTooltipProps> = ({ userId, onSuccess }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  const handleConnect = async () => {
    if (!apiKey.trim()) {
      setError('Please enter your API key');
      return;
    }

    setConnecting(true);
    setError('');

    try {
      const response = await fetch('/api/marketing/bing/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey: apiKey.trim(), userId })
      });

      const result = await response.json();

      if (result.success) {
        setShowTooltip(false);
        setApiKey('');
        if (onSuccess) onSuccess();
      } else {
        setError(result.message || 'Invalid API key');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="relative inline-block">
      {/* Trigger Button */}
      <button
        onClick={() => setShowTooltip(!showTooltip)}
        className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
      >
        <Key className="w-4 h-4" />
        Connect Bing
        <HelpCircle className="w-4 h-4 opacity-70" />
      </button>

      {/* Tooltip Modal */}
      {showTooltip && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/30 z-[9998] flex items-center justify-center" 
            onClick={() => setShowTooltip(false)}
          />
          
          {/* Modal Content */}
          <div 
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-full max-w-md bg-white rounded-lg shadow-2xl border border-gray-200 p-4 mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">Connect Bing Webmaster Tools</h3>
              <button 
                onClick={() => setShowTooltip(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Instructions */}
            <div className="space-y-3 mb-4">
              <div className="bg-blue-50 rounded-lg p-3 text-xs">
                <p className="font-medium text-blue-900 mb-2">📝 How to get your API key:</p>
                <ol className="space-y-1.5 text-blue-800">
                  <li className="flex gap-2">
                    <span className="font-semibold">1.</span>
                    <span>
                      Visit{' '}
                      <a 
                        href="https://www.bing.com/webmasters" 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-blue-600 inline-flex items-center gap-0.5"
                      >
                        Bing Webmaster Tools
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-semibold">2.</span>
                    <span>Sign in with Microsoft account</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-semibold">3.</span>
                    <span>Add & verify your website</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-semibold">4.</span>
                    <span>Go to <strong>Settings → API Access</strong></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-semibold">5.</span>
                    <span>Click <strong>"Get API Key"</strong></span>
                  </li>
                </ol>
              </div>

              {/* API Key Input */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Paste your API key:
                </label>
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => {
                    setApiKey(e.target.value);
                    setError('');
                  }}
                  placeholder="ABC123XYZ456..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleConnect();
                  }}
                />
                <p className="mt-1 text-xs text-gray-500">
                  🔒 Encrypted and stored securely
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
            </div>

            {/* Action Button */}
            <button
              onClick={handleConnect}
              disabled={!apiKey || connecting}
              className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium transition-colors"
            >
              {connecting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Verifying...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Connect
                </>
              )}
            </button>

            {/* Footer */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-600">
                ✨ <strong>Free:</strong> 100k requests/month • Multiple sites • Real search data
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BingConnectionTooltip;
